import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import template from './ns-filtered-address.html';

import {addressData} from './addressData.js';

// import '@vaadin/vaadin-combo-box/vaadin-combo-box.js'; // we want to switch to this once the dependencies with Polymer components can be reconciled

/**
 *  `ns-filtered-address` replaces ns-city-state-zip for addresses that require a
 *  filtered address - select a region, then select a district based on that region.
 *  This could be utilized in other ways: province -> city, city -> neighborhood, etc.
 *  Example use cases are Peru and Vietnam.
 *  This component is included in ns-address and can be utilized by adding the use-filtered-address attribute.
 *
 *  @demo playground/ns-filtered-address-demo.html Demo including ns-address integration
 */
class NsFilteredAddress extends PolymerElement {
    constructor() {
        super();
    }

    static get is() {
        return "ns-filtered-address";
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * The city from the input box
             */
            city: {
                type: String,
                notify: true
            },
            /**
             * The label for the city input
             */
            cityLabel: {
                type: String,
                value: 'City'
            },
            /**
             * The placeholder for the city input
             */
            cityPlaceholder: {
                type: String,
                value: 'City'
            },
            /**
             * Used to indicate country for localization
             */
            country: {
                type: String,
                observer: 'setStateList'
            },
            /**
             * The currently selected distrcit
             */
            currentDistrict: {
                type: String,
                value: 'Example District'
            },
            /**
             * The currently selected region
             */
            currentRegion: {
                type: String,
                value: 'Example Region'
            },
            /**
             * The currently selected district
             */
            district: {
                type: String,
                notify: true
            },
            /**
             * The list of districts
             */
            districts: {
                type: Array,
                value: [{id: 'test1', text: 'test1'}, {id: 'test2', text: 'test2'}, {id: 'test3', text: 'test3'}, {id: 'test4', text: 'test4'}]
            },
            /**
             * The label for the districts input
             */
            districtsLabel: {
                type: String,
                value: 'District'
            },
            /**
             * The currently selected region
             */
            region: {
                type: String,
                notify: true
            },
            /**
             * The object representing a heirarchical list of regions with thier districts
             */
            regionDistrictData: {
                type: Object
            },
            /**
             * The list of regions
             */
            regions: {
                type: Array,
                value: [{id: 'test1', text: 'test1'}, {id: 'test2', text: 'test2'}, {id: 'test3', text: 'test3'}, {id: 'test4', text: 'test4'}]
            },
            /**
             * The label for the regions input
             */
            regionsLabel: {
                type: String,
                value: 'Region'
            },
            /**
             * The currently selected region. It is duplicated as state for forms that are expecting state for SAP
             */
            state: {
                type: String,
                notify: true
            },
            /**
             * The zip from the input box
             */
            zip: {
                type: String,
                notify: true
            },
            /**
             * The label for the city input
             */
            zipLabel: {
                type: String,
                value: 'Zip'
            },
            /**
             * The placeholder for the zip input
             */
            zipPlaceholder: {
                type: String,
                value: 'Zip'
            },
            /**
             * Dirty flag for ns-filtered-address
             */
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(regionDirty, districtDirty, cityDirty, zipDirty)'
        ];
    }

    /**
     * Does the setup work for the component. Like...
     */
    ready() {
        super.ready();
        // Grab the data and store it in the property
        this.regionDistrictData = addressData['PE'];
        this._extractRegions(this.regionDistrictData);
        console.log('in filtered ready');
    }

    connectedCallback() {
        super.connectedCallback();
        this.$.regionInput.addEventListener('nsDropdownSelect', e => {
            this._handleSelectedRegion(e);
        });
        this.$.districtInput.addEventListener('nsDropdownSelect', e => {
            this._handleSelectedDistrict(e);
        });
    }

    /**
     * A convenient way to get all the values for this component as an object
     */
    getAllValues() {
        let region = this.$.regionInput.selectedText;
        let district = this.$.districtInput.selectedText;
        let city = this.$.city.value;
        let zip = this.$.zip.value;
        let values = {
            region: region,
            district: district,
            city: city,
            zip: zip
        }
        return values;
    }

    /**
     * Leverages the ns-form validation
     */
    validate() {
        return this.shadowRoot.querySelector("ns-form").validate();
    }

    /**
     * Extracts a list of regions from a address object and assigns them to the regions property
     * @param {Address Object} data
     */
    _extractRegions(data) {
        let regions = [];
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const region = data[key];
                regions.push({
                    id: region.number,
                    text: key
                });
            }
        }
        regions.sort((a, b) => (a.text > b.text) ? 1 : ((b.text > a.text) ? -1 : 0));
        this.regions = regions;
    }

    /**
     * Extracts a list of districts from a the regionDistritData property given
     * a specified region and assigns them to the districts property
     * @param {Address Object} data
     */
    _extractDistricts(region) {
        let districts = [];
        let data = this.regionDistrictData;
        data[region].districts.forEach((district, index) => {
            districts.push({
                id: 'district' + index,
                text: district
            });
        });
        districts.sort((a, b) => (a.text > b.text) ? 1 : ((b.text > a.text) ? -1 : 0));
        this.districts = districts;
    }

    /**
     * Populates the districts dropdown based on the selected region.
     * Sets the selected item as the region and state
     * @param {Event Object} e
     */
    _handleSelectedRegion(e) {
        let region = e.detail.selectedItem.text;
        this.region = this.state = e.detail.selectedItem.value;
        console.log('handling selected region', this.region, this.state);
        this.$.districtInput.clear();
        this._extractDistricts(region);
        if (this.$.districtInput.disabled) {
            this.$.districtInput.removeAttribute('disabled');
        }
    }

    /**
     * Populates the districts dropdown based on the selected region
     * @param {Event Object} e
     */
    _handleSelectedDistrict(e) {
        let district = e.detail.selectedItem.text;
        this.district = district;
        console.log('handling selected district', this.district);
    }
}

customElements.define(NsFilteredAddress.is, NsFilteredAddress);